type EnvSource = Record<string, string | undefined>;

export interface ConfigField<T> {
  key: string;
  parse: (rawValue: string | undefined) => T;
}

type ConfigSchema = Record<string, ConfigField<unknown>>;

type InferConfig<TSchema extends ConfigSchema> = {
  [TKey in keyof TSchema]: TSchema[TKey] extends ConfigField<infer TValue> ? TValue : never;
};

interface StringOptions {
  defaultValue?: string;
}

interface BooleanOptions {
  defaultValue?: boolean;
}

interface CsvOptions {
  defaultValue?: string;
  filterEmpty?: boolean;
  normalize?: (value: string) => string;
}

interface DelimitedEnumOptions {
  defaultValue: string;
}

const defaultCsvNormalize = (value: string): string => value.trim();

function createField<T>(key: string, parse: (rawValue: string | undefined) => T): ConfigField<T> {
  return { key, parse };
}

function parseDelimitedEnumValues<T extends string>(
  rawValue: string | undefined,
  validValues: readonly T[],
  fallbackValue: string,
): T[] {
  const validValueSet = new Set<T>(validValues);

  return (rawValue ?? fallbackValue)
    .split(',')
    .map((value: string) => value.trim().toLowerCase())
    .filter((value: string): value is T => validValueSet.has(value as T));
}

export const env = {
  string(key: string, options: StringOptions = {}): ConfigField<string> {
    return createField(
      key,
      (rawValue: string | undefined) => rawValue ?? options.defaultValue ?? '',
    );
  },

  boolean(key: string, options: BooleanOptions = {}): ConfigField<boolean> {
    return createField(key, (rawValue: string | undefined) =>
      rawValue === undefined ? (options.defaultValue ?? false) : rawValue === 'true',
    );
  },

  notFalse(key: string): ConfigField<boolean> {
    return createField(key, (rawValue: string | undefined) => rawValue !== 'false');
  },

  csv(key: string, options: CsvOptions = {}): ConfigField<string[]> {
    return createField(key, (rawValue: string | undefined) => {
      const normalize = options.normalize ?? defaultCsvNormalize;
      const filterEmpty = options.filterEmpty ?? true;
      const input = rawValue ?? options.defaultValue ?? '';
      const values = input.split(',').map(normalize);

      return filterEmpty ? values.filter((value: string) => value.length > 0) : values;
    });
  },

  delimitedEnumValues<T extends string>(
    key: string,
    validValues: readonly T[],
    options: DelimitedEnumOptions,
  ): ConfigField<T[]> {
    return createField(key, (rawValue: string | undefined) =>
      parseDelimitedEnumValues(rawValue, validValues, options.defaultValue),
    );
  },

  custom<T>(key: string, parse: (rawValue: string | undefined) => T): ConfigField<T> {
    return createField(key, parse);
  },
};

export function loadConfig<TSchema extends ConfigSchema>(
  source: EnvSource,
  schema: TSchema,
): InferConfig<TSchema> {
  const configEntries = Object.entries(schema).map(([propertyName, field]) => [
    propertyName,
    field.parse(source[field.key]),
  ]);

  return Object.fromEntries(configEntries) as InferConfig<TSchema>;
}
